import { type PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CashierUserData } from "@/api/cashier";
import { FeatureFlag, initLaunchDarkly } from "@/api/launch-darkly";
import { useUserData } from "@/hooks/useUserData";
import { getLegacyCashierUrl } from "@/utils/constants";
import { SkeletonLoadingScreen } from "@/components/skeleton-loading-screen/SkeletonLoadingScreen";

const anonymizeUserProfile = (
  profile: CashierUserData
): Record<string, string> => {
  if (
    import.meta.env.MODE !== "production" ||
    profile.email.endsWith("@pepperstone.com")
  ) {
    // only send PII in non-production mode or when handling company accounts
    return {
      name: profile.name,
      email: profile.email,
    };
  }

  return {};
};

const checkMainFeatureFlag = async ({
  isLoading,
  profile,
  locale,
}: {
  profile: CashierUserData;
  isLoading: boolean;
  locale: string;
}) => {
  if (isLoading || !profile) return true;

  const ldClient = await initLaunchDarkly({
    context: {
      key: `SCA_${profile.sca_id}`,
      scaId: `${profile.sca_id}`,
      language: locale,
      country: profile.country_code,
      licence: profile.license_name,
      ...anonymizeUserProfile(profile),
    },
  });

  const pnxCashierEnabled = ldClient.variation(
    FeatureFlag.FLAG_PNX_CASHIER,
    false
  ) as boolean;

  if (!pnxCashierEnabled) {
    const url = getLegacyCashierUrl({ locale });
    console.info("PNX Cashier is not enabled, redirecting to SCA Cashier", {
      currentUrl: window.location.href,
      url,
    });
    window.location.href = url;
    return false;
  }

  return true;
};

// TODO replace with provider
export const FeatureFlagGuard = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();
  const { isLoading, data: profile } = useUserData();

  const [featureFlagCheck, setFeatureFlagCheck] = useState(false);

  if (!featureFlagCheck) {
    checkMainFeatureFlag({
      isLoading,
      profile,
      locale: i18n.language,
    }).then((result) => {
      if (result) {
        setFeatureFlagCheck(true);
      }
    });
    return (
      <section className="cashier">
        <div className="cashier-mfe-wrapper">
          <SkeletonLoadingScreen />
        </div>
      </section>
    );
  }

  return children;
};
